import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fab,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertDialog,
  DragAndDropStack,
  type DraftExecutivePosition,
  ExecutivePositionForm,
  IconButtonWithTooltip,
  MemberStaffCard,
  PersonSelectDialog,
  PersonMiniCard,
  type PersonAddress,
  useExecRoleLuts,
  useGlobalMessageHandler,
  useProtectedRoleHoldersLut,
  useUsLegRoleTypeLuts,
  useUsPrefixLuts,
  useUsRoleTypeLuts
} from 'components'
import { useSessionContext } from 'session'
import { BulkAssignButton } from 'components/us/bulk-assign/bulk-assign-button'
import { formatErrorFieldMessages } from 'interfaces'
import { formatFullName, getEmptyPersonAddress, getEmptyPersonPhone, getEmptyStafferEmail, groupStaffersByRole, isExecPositionRowVacant } from 'helpers'
import {
  bulkAssignExecPositionStaffers,
  deleteVacantExecutivePosition,
  type ExecutiveStaffInformation,
  type FillPersonPayload,
  fillExecutivePosition,
  getExecutivePosition,
  getStaffByExecutivePosition,
  reorderExecutiveStaffers,
  retireExecutivePosition,
  updateExecutivePosition
} from 'services/executives'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { getProtectedRoleHolders } from 'services'
import { createPersonAddress } from 'services/person/us/edit/personAddressService'
import { type PersonBaseBasicInformation } from 'services/person/international/edit/personBasicInfoService'
import { getExecPositionFieldErrors, getExecStafferRoleId, getStatusTitleWarning, PHONE_TYPES } from 'utils'

const toDraft = (position: ExecutivePosition): DraftExecutivePosition => ({
  ...position,
  branchRoleId: position.execRole.role.id
})

export const ExecutivePositionUpdatePage: React.FC = () => {
  const { positionId } = useParams<{ positionId: string }>()
  const parsedPositionId = positionId != null ? parseInt(positionId, 10) : 0
  const navigate = useNavigate()
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { lutValues: execRoleLut } = useExecRoleLuts()
  const { lutValues: roleTypeLutValues } = useUsRoleTypeLuts()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const { lutValues: legRoleLut, loading: loadingLegRoleTypeLutValues } = useUsLegRoleTypeLuts()
  const { setLutValues: setProtectedRoleHoldersLut } = useProtectedRoleHoldersLut()
  const [originalPosition, setOriginalPosition] = useState<ExecutivePosition | null>(null)
  const [position, setPosition] = useState<DraftExecutivePosition | null>(null)
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showRetireConfirmation, setShowRetireConfirmation] = useState(false)
  const [retiring, setRetiring] = useState(false)
  const [showFillDialog, setShowFillDialog] = useState(false)
  const [filling, setFilling] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [staffers, setStaffers] = useState<ExecutiveStaffInformation[]>([])
  const [creatingStaff, setCreatingStaff] = useState<ExecutiveStaffInformation | undefined>(undefined)
  const [showAddStafferDialog, setShowAddStafferDialog] = useState(false)
  const [personAddresses, setPersonAddresses] = useState<PersonAddress[]>([])

  useEffect(() => { changeModuleName('Edit Executive Position') }, [changeModuleName])

  useEffect(() => {
    if (Number.isNaN(parsedPositionId) || parsedPositionId === 0) {
      setLoading(false)
      return
    }
    const positionRequest = getExecutivePosition(parsedPositionId)
      .then(response => {
        if (response.succeeded && response.result != null) {
          setOriginalPosition(response.result)
          setPosition(toDraft(response.result))
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to load position.')
        }
      })
    const staffersRequest = getStaffByExecutivePosition(parsedPositionId)
      .then(response => {
        if (response.succeeded && response.result != null) {
          setStaffers(response.result)
        } else if (!response.succeeded) {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to load staffers.')
        }
      })
    Promise.all([positionRequest, staffersRequest])
      .finally(() => { setLoading(false) })
  }, [parsedPositionId])

  useEffect(() => {
    changeUniqueId(position != null ? `${parsedPositionId} - ${position.title}` : '')
  }, [position, parsedPositionId, changeUniqueId])

  const generalRoleId = position?.branchRoleId ?? null

  const handleAddStafferPersonSelected = (person: PersonBaseBasicInformation): void => {
    if (position == null || generalRoleId == null) return
    const branchRole = roleTypeLutValues.roles.get(generalRoleId)
    if (branchRole == null) return
    const stafferRoleId = getExecStafferRoleId(generalRoleId)
    if (stafferRoleId == null) return
    const defaultExecRole = execRoleLut.execRoles.find(r => r.role.id === stafferRoleId)
    if (defaultExecRole == null) return
    setShowAddStafferDialog(false)

    const emptyAddress = getEmptyPersonAddress(person.id ?? 0)
    const defaultPhone = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultPhone.typeId = PHONE_TYPES.PHONE
    const defaultFax = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultFax.typeId = PHONE_TYPES.FAX
    emptyAddress.phones = [defaultPhone, defaultFax]

    setCreatingStaff({
      id: 0,
      person,
      role: { ...branchRole, id: stafferRoleId },
      execrole: defaultExecRole,
      execrole2: null,
      execrole3: null,
      execpos: { id: parsedPositionId, title: position.title ?? null },
      statusId: undefined,
      title: undefined,
      email: getEmptyStafferEmail(person.id ?? 0),
      address: emptyAddress,
      displayOrder: 0,
      updatedBy: undefined,
      lastUpdated: undefined,
      responsibilities: []
    })
  }

  const addStafferToList = (created: ExecutiveStaffInformation): void => {
    setStaffers(prev => [...prev, created])
    setCreatingStaff(undefined)
  }

  if (Number.isNaN(parsedPositionId) || parsedPositionId === 0) {
    return <Box>Invalid Executive Position ID</Box>
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}><CircularProgress size={100} /></Box>
  }
  if (position == null || originalPosition == null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
        <Stack spacing={2} alignItems='center' mt={1} width='98%'>
          <Typography>Executive Position not found.</Typography>
        </Stack>
      </Box>
    )
  }

  const isVacant = isExecPositionRowVacant(position.person)
  const electionBlocked = Object.keys(getExecPositionFieldErrors(position, isVacant)).length > 0
  const fieldsDisabled = !editing
  const statusTitleWarning = isVacant ? null : getStatusTitleWarning(position.statusId, position.title)
  const positionDefaultAddress = personAddresses.find(address => address.id === position.addressId) ?? null

  const update = (changes: Partial<DraftExecutivePosition>): void => {
    setPosition(prev => (prev != null ? { ...prev, ...changes } : prev))
  }

  const persistNewAddress = async (draft: PersonAddress): Promise<PersonAddress | null> => {
    const ownerId = position.person.id
    if (ownerId == null) return null
    const response = await createPersonAddress(ownerId, draft)
    if (response.succeeded && response.result != null) {
      showSuccessMessage('Address created.')
      return response.result
    }
    const errorMessage = formatErrorFieldMessages(response.errors)
    showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to create address.')
    return null
  }

  const handleStartEditing = (): void => { setEditing(true) }

  const handleCancel = (): void => {
    setPosition(originalPosition != null ? toDraft(originalPosition) : null)
    setEditing(false)
  }

  const handleSave = (): void => {
    if (position.execRole == null) return
    const payload: ExecutivePosition = { ...position, execRole: position.execRole }
    setSaving(true)
    updateExecutivePosition(parsedPositionId, payload)
      .then(response => {
        if (response.succeeded && response.result != null) {
          setOriginalPosition(response.result)
          setPosition(toDraft(response.result))
          setEditing(false)
          showSuccessMessage('Position updated.')
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to update position.')
        }
      })
      .finally(() => { setSaving(false) })
  }

  const handleRetire = (thenFill: boolean): void => {
    setShowRetireConfirmation(false)
    setRetiring(true)
    retireExecutivePosition(parsedPositionId)
      .then(response => {
        if (response.succeeded && response.result != null) {
          setOriginalPosition(response.result)
          setPosition(toDraft(response.result))
          showSuccessMessage('Executive retired. Position is now vacant.')
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
          if (thenFill) setShowFillDialog(true)
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to retire executive.')
        }
      })
      .finally(() => { setRetiring(false) })
  }

  // After delete the position no longer exists; redirect to the parent org
  // so the editor lands somewhere meaningful (and sees the surviving
  // positions with their freshly-densened display order). Navigate
  // BEFORE the success toast so the position page is on its way out
  // before the provider re-renders.
  const handleDelete = (): void => {
    setShowDeleteConfirmation(false)
    setDeleting(true)
    const parentOrgId = position.org?.id ?? null
    deleteVacantExecutivePosition(parsedPositionId)
      .then(response => {
        if (response.succeeded) {
          if (parentOrgId != null) {
            navigate(`/us/executives/orgs/${parentOrgId}`)
          } else {
            navigate('/us/executives/positions')
          }
          showSuccessMessage('Vacant position deleted.')
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to delete position.')
          setDeleting(false)
        }
      })
      .catch(() => {
        showErrorMessage('Failed to delete position.')
        setDeleting(false)
      })
  }

  const runFill = (person: FillPersonPayload): void => {
    setFilling(true)
    fillExecutivePosition(parsedPositionId, person)
      .then(response => {
        if (response.succeeded && response.result != null) {
          setOriginalPosition(response.result)
          setPosition(toDraft(response.result))
          const filled = response.result.person
          showSuccessMessage(`Position filled with ${`${filled.firstName ?? ''} ${filled.lastName}`.trim()}.`)
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to fill position.')
        }
      })
      .finally(() => { setFilling(false) })
  }

  // Existing person (has id) → fill directly; the "Add New Person" path returns
  // an id-less person, which the backend creates inline from first/last name.
  const handleFill = (person: PersonBaseBasicInformation): void => {
    setShowFillDialog(false)
    runFill(
      person.id != null && person.id !== 0
        ? { id: person.id, firstName: person.firstName ?? '', lastName: person.lastName }
        : { firstName: person.firstName ?? '', lastName: person.lastName }
    )
  }

  const holder = (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={9}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap' }}>Current Holder:</Typography>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <PersonMiniCard
              person={position.person}
              filled={!isVacant}
              withPhoto
              fullWidth
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!retiring && !isVacant && (
          <IconButtonWithTooltip
            label='Retire Executive'
            color='secondary'
            onClick={() => { setShowRetireConfirmation(true) }}
            disabled={editing || saving}
            icon={<HourglassEmptyIcon />}
          />
        )}
        {!filling && isVacant && (
          <IconButtonWithTooltip
            label='Fill Position'
            color='primary'
            onClick={() => { setShowFillDialog(true) }}
            disabled={editing || saving || deleting}
            icon={<PersonAddIcon />}
          />
        )}
        {!deleting && isVacant && (
          <IconButtonWithTooltip
            label='Delete Vacant Position'
            sx={{ color: 'error.main' }}
            onClick={() => { setShowDeleteConfirmation(true) }}
            disabled={editing || saving || filling}
            icon={<DeleteIcon />}
          />
        )}
        {(retiring || filling || deleting) && <CircularProgress size={24} />}
      </Grid>
    </Grid>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
      <Stack spacing={2} mt={1} width='98%'>
        <Card>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 2 }}>Position Information</Typography>
            {!isVacant && position.addressId == null && !editing && (
              <Typography variant='body2' color='warning.main' sx={{ mb: 1 }}>
                This position has no address linked. Click Edit to attach one.
              </Typography>
            )}
            {statusTitleWarning != null && (
              <Typography variant='body2' color='warning.main' sx={{ mb: 1 }}>
                {statusTitleWarning} Update the title or status so they stay consistent.
              </Typography>
            )}
            {electionBlocked && (
              <Typography variant='body2' color='warning.main' sx={{ mb: 1 }}>
                This elected position has missing or invalid election fields; fix them or change Position Type before saving.
              </Typography>
            )}
            <ExecutivePositionForm
              position={position}
              update={update}
              fieldsDisabled={fieldsDisabled}
              persistNewAddress={persistNewAddress}
              allowOrgChange
              isVacant={isVacant}
              holder={holder}
              onAddressOptionsLoaded={setPersonAddresses}
              excludePositionId={position.id}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 4 }}>
              {editing && position.execRole == null && (
                <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                  Select an Executive Role to save.
                </Typography>
              )}
              {saving && <CircularProgress size={28} sx={{ alignSelf: 'center', mr: 1 }} />}
              {!editing && (
                <IconButtonWithTooltip
                  label='Edit Position'
                  color='primary'
                  onClick={handleStartEditing}
                  disabled={saving || retiring || filling || deleting}
                  icon={<EditIcon />}
                />
              )}
              {editing && (
                <IconButtonWithTooltip
                  label='Save Position'
                  color='primary'
                  onClick={handleSave}
                  disabled={saving || position.title.trim() === '' || position.execRole == null || electionBlocked}
                  icon={<SaveIcon />}
                />
              )}
              {editing && (
                <IconButtonWithTooltip
                  label='Cancel Editing'
                  color='secondary'
                  onClick={handleCancel}
                  disabled={saving}
                  icon={<CloseIcon />}
                />
              )}
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 2 }}>Staffers</Typography>
            {creatingStaff == null && (
              <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 2, mb: 2 }}>
                <BulkAssignButton
                  ownerDisplayName={formatFullName(position.person, usPrefixLutValues.prefixes)}
                  targetLabel='office'
                  buttonLabel='Apply address & phones to all staffers'
                  dialogTitle='Apply position contact to all staffers'
                  staffers={staffers.filter(s => s.address.id > 0)}
                  hasSourceAddress={position.addressId != null}
                  onConfirm={async () => await bulkAssignExecPositionStaffers(parsedPositionId)}
                  onSuccess={() => {
                    getStaffByExecutivePosition(parsedPositionId)
                      .then(response => {
                        if (response.succeeded && response.result != null) {
                          setStaffers(response.result)
                        } else {
                          showErrorMessage('Staffers updated, but the list failed to refresh — reload to see the changes.')
                        }
                      })
                      .catch(() => { showErrorMessage('Staffers updated, but the list failed to refresh — reload to see the changes.') })
                  }}
                />
                <Fab color='primary' aria-label='add' onClick={() => { setShowAddStafferDialog(true) }}>
                  <AddIcon />
                </Fab>
              </Box>
            )}
            {staffers.length === 0
              ? (
                <Typography variant='body2' color='text.secondary'>No staffers attached.</Typography>
                )
              : (
                <Stack spacing={2}>
                  {groupStaffersByRole(staffers, s => s.execrole).map(([roleId, group]) => (
                    <DragAndDropStack
                      key={roleId}
                      id={`exec-staff-${parsedPositionId}-${roleId}`}
                      originalItems={group}
                      itemRenderer={(s) => (
                        <MemberStaffCard
                          key={s.id}
                          createCard={false}
                          originalPosition={s}
                          legRoleLuts={legRoleLut}
                          loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                          execRoleLuts={execRoleLut}
                          execPositionAddress={positionDefaultAddress}
                          onStaffUpdate={(updated) => { setStaffers(prev => prev.map(existing => existing.id === updated.id ? updated : existing)) }}
                          onRetire={(retired) => { setStaffers(prev => prev.filter(existing => existing.id !== retired.id)) }}
                        />
                      )}
                      updateDisplayOrder={async (items, itemSelected) => {
                        const idAndDisplayOrderMap = items.reduce<Record<number, number>>((acc, s, index) => ({ ...acc, [s.id]: index + 1 }), {})
                        const response = await reorderExecutiveStaffers(parsedPositionId, idAndDisplayOrderMap, itemSelected.id, itemSelected.execrole.id)
                        if (response.succeeded) {
                          setStaffers(prev => prev.map(s => idAndDisplayOrderMap[s.id] != null ? { ...s, displayOrder: idAndDisplayOrderMap[s.id] } : s))
                        }
                        return response
                      }}
                      disableDrag={group.length <= 1}
                      makeGray={false}
                    />
                  ))}
                </Stack>
                )}
            {creatingStaff != null && (
              <Box mt={2}>
                <MemberStaffCard
                  key='new-exec-staff-card'
                  createCard={true}
                  originalPosition={creatingStaff}
                  legRoleLuts={legRoleLut}
                  loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                  execRoleLuts={execRoleLut}
                  execPositionAddress={positionDefaultAddress}
                  onStaffUpdate={setCreatingStaff}
                  cancelCreate={() => { setCreatingStaff(undefined) }}
                  addToExistingStaffers={(created) => { addStafferToList(created) }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>
      <AlertDialog
        open={showRetireConfirmation}
        title='Retire Executive'
        contentText='This will mark the position as Vacant. Address and phone records are preserved on the new sentinel. Fill it now, or leave it open?'
        nameButtonConfirm='Retire & Fill Now'
        nameButtonSecondary='Retire & Hold Open'
        nameButtonCancel='Cancel'
        onButtonClick={() => { handleRetire(true) }}
        onSecondaryClick={() => { handleRetire(false) }}
        onClose={() => { setShowRetireConfirmation(false) }}
        containedButton
      />
      <AlertDialog
        open={showDeleteConfirmation}
        title='Delete Vacant Position'
        contentText={`This will permanently delete the vacant position "${position.title}" and renumber the remaining positions in this organization. This cannot be undone. Are you sure?`}
        nameButtonConfirm='Delete'
        nameButtonCancel='Cancel'
        onButtonClick={handleDelete}
        onClose={() => { setShowDeleteConfirmation(false) }}
        containedButton
      />
      <PersonSelectDialog<PersonBaseBasicInformation>
        open={showFillDialog}
        onClose={() => { setShowFillDialog(false) }}
        onPersonSelected={handleFill}
        title='Fill Position — Select Person'
        personType='personUS'
      />
      <PersonSelectDialog<PersonBaseBasicInformation>
        open={showAddStafferDialog}
        onClose={() => { setShowAddStafferDialog(false) }}
        onPersonSelected={handleAddStafferPersonSelected}
        title='Add Staffer — Select Person'
        personType='personUS'
      />
    </Box>
  )
}
